// Serviço para buscar imagens de fachadas usando proxy backend (Google Street View)
import { inspectionClient } from '@/config/api';

interface GeocodingResult {
  lat: number;
  lng: number;
  formattedAddress: string;
}

class StreetViewService {
  /**
   * Geocodifica um endereço via backend proxy
   */
  private async geocodeAddress(address: string): Promise<GeocodingResult> {
    console.log('[StreetView] Geocoding endereço:', address);
    try {
      const response = await inspectionClient.get<GeocodingResult>('/api/street-view/geocode', {
        params: { address },
      });
      console.log('[StreetView] Geocode OK:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[StreetView] Geocode ERRO:', error?.response?.status, error?.response?.data, 'endereço:', address);
      throw error;
    }
  }

  /**
   * Busca imagem do Street View via backend proxy e converte para base64
   */
  private async fetchImageAsBase64(lat: number, lng: number, heading: number): Promise<string> {
    console.log(`[StreetView] Buscando imagem: lat=${lat}, lng=${lng}, heading=${heading}`);
    try {
      const response = await inspectionClient.get('/api/street-view/image', {
        params: { lat, lng, heading },
        responseType: 'blob',
      });

      console.log(`[StreetView] Imagem recebida: ${(response.data as Blob).size} bytes`);

      const blob = response.data as Blob;
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Erro ao converter imagem'));
        reader.readAsDataURL(blob);
      });
    } catch (error: any) {
      console.error(`[StreetView] Imagem ERRO: ${error?.response?.status}`, error?.response?.data);
      throw error;
    }
  }

  /**
   * Busca múltiplas imagens do Street View em diferentes ângulos
   */
  public async fetchMultipleStreetViewAngles(
    address: string
  ): Promise<Array<{ heading: number; base64: string; label: string }>> {
    try {
      const location = await this.geocodeAddress(address);

      const angles = [
        { heading: 0, label: 'Norte' },
        { heading: 90, label: 'Leste' },
        { heading: 180, label: 'Sul' },
        { heading: 270, label: 'Oeste' },
      ];

      const images = await Promise.all(
        angles.map(async (angle) => {
          const base64 = await this.fetchImageAsBase64(location.lat, location.lng, angle.heading);
          return {
            heading: angle.heading,
            base64,
            label: angle.label,
          };
        })
      );

      return images;
    } catch (error: unknown) {
      throw new Error(error instanceof Error ? error.message : 'Erro ao buscar imagens do endereço');
    }
  }

  /**
   * Busca imagem do Street View com ângulo específico
   */
  public async fetchStreetImageAsBase64(address: string, heading: number = 0): Promise<string> {
    try {
      const location = await this.geocodeAddress(address);
      return await this.fetchImageAsBase64(location.lat, location.lng, heading);
    } catch (error: unknown) {
      throw new Error(error instanceof Error ? error.message : 'Erro ao buscar imagem do endereço');
    }
  }

  /**
   * Verifica se o Street View está disponível para o endereço
   */
  public async checkAddressAvailability(
    address: string
  ): Promise<{ available: boolean; lat?: number; lng?: number; message?: string }> {
    try {
      const location = await this.geocodeAddress(address);
      return {
        available: true,
        lat: location.lat,
        lng: location.lng,
        message: `Endereço encontrado: ${location.formattedAddress}`,
      };
    } catch (error: unknown) {
      return {
        available: false,
        message: error instanceof Error ? error.message : 'Erro ao verificar endereço',
      };
    }
  }
}

export const streetViewService = new StreetViewService();
